import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../framework/redux/hooks";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { Base } from "../../framework/common/base";
import { useNavigate } from "react-router-dom";
import {
    fetchTransportNotifications,
    fetchGenericNotifications,
    setLastUnconfirmedCount,
    confirmGenericNotificationThunk
} from "../../framework/redux/slices/notificationSlice";
import { useSelectWorkShiftNotifications } from "../../hooks/notification/useSelectWorkShiftNotifications";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { NotificationsPageTitle } from "../layout/notificationsPageTitle";
import { transportOrderStateNames } from "../../models/transport/transportOrder";
import { useSelector } from "react-redux";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import {
    optionOwnerHasTransportNotificationsEnabled,
    OptionPublishedWorkShiftNotificationsEnabled,
} from "../../models/common/consts";
import { AppNotification, GenericNotificationDto } from "../../models/notification/notification";
import { addAlert } from "../../framework/redux/slices/appSlice";
import { AlertSeverity } from "../../models/common/enums";

export const Notifications = () => {
    const [orderNotificationsEnabled, optionLoading] = useAppOptionValueEquals(
        optionOwnerHasTransportNotificationsEnabled,
        true
    );
    const [plannedShiftNotificationsEnabled] = useAppOptionValueEquals(
        OptionPublishedWorkShiftNotificationsEnabled,
        true
    );
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const employeeId = useAppSelector((state) => state.session.id);
    const user = useAppSelector(({ session }) => session.fullName);
    const notifications: AppNotification[] = useAppSelector((state): any[] => state.notification.notifications);
    const notificationsArr: AppNotification[] = Array.isArray(notifications) ? notifications : [];
    const workShiftNotifications: GenericNotificationDto[] = useSelectWorkShiftNotifications();
    const loading = useAppSelector((state) => state.notification.loading);
    const lastUnconfirmedCount: number = useSelector(
        (state: any) => state.notification.lastUnconfirmedCount as number
    );

    useEffect(() => {
        if (
            !(
                optionLoading ||
                orderNotificationsEnabled ||
                plannedShiftNotificationsEnabled
            )
        ) {
            navigate("/", { replace: true });
        }
    }, [optionLoading, orderNotificationsEnabled, plannedShiftNotificationsEnabled]);

    const groupedNotifications: Record<string, any[]> = notificationsArr.reduce((acc: Record<string, any[]>, notification: any) => {
        const transportOrderId = notification.transportOrderId;
        if (!transportOrderId) return acc;
        if (!acc[transportOrderId]) {
            acc[transportOrderId] = [];
        }
        acc[transportOrderId].push(notification);
        return acc;
    }, {});

    useEffect(() => {
        if (plannedShiftNotificationsEnabled) {
            void dispatch(fetchGenericNotifications());
        }

        if (orderNotificationsEnabled) {
            void dispatch(fetchTransportNotifications(employeeId));
        }
    }, [
        dispatch,
        employeeId,
        orderNotificationsEnabled,
        plannedShiftNotificationsEnabled,
    ]);

    useEffect(() => {
        if (loading) return;

        // Hack to update lastUnconfirmedCount
        if (lastUnconfirmedCount > Object.keys(groupedNotifications).length) {
            void dispatch(setLastUnconfirmedCount(Object.keys(groupedNotifications).length));
        }

    }, [groupedNotifications]);

    if (loading) {
        return (
            <MainLayout
                topComponent={<PageTitle header={t("notifications.notifications")} />}
                hasPx
            >
                <Typography>{t("common.loading")}</Typography>
            </MainLayout>
        );
    }

    const renderWorkShiftNotifications = () => {
        if (workShiftNotifications.length === 0) return null;

        return (
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    {t("notifications.workShifts")}
                </Typography>

                {workShiftNotifications.map(notification => {
                    let context: { startDate?: string; endDate?: string; changedDates?: string[] } = {};

                    try {
                        context = notification.contextJson
                            ? JSON.parse(notification.contextJson)
                            : {};
                    } catch {
                        context = {};
                    }

                    const translationKey = String(notification.messageKey);
                    const type = String(notification.type || "");
                    const startDate = context.startDate ? Base.dateToDateStr(context.startDate) : undefined;
                    const endDate = context.endDate ? Base.dateToDateStr(context.endDate) : undefined;
                    const changedDatesRaw = Array.isArray(context.changedDates) ? context.changedDates : [];
                    const changedDates = changedDatesRaw.map((d) => Base.dateToDateStr(d));

                    let displayMessage: string = "";
                    if (type === "workShift.shiftsChanged") {
                        const count = changedDates.length;
                        if (!count || count > 3) {
                            displayMessage = t("notifications.shiftsUpdated.period", { start: startDate, end: endDate });
                        } else if (count === 1) {
                            displayMessage = t("notifications.shiftChanged.single", { date: changedDates[0] });
                        } else if (count === 2) {
                            displayMessage = t("notifications.shiftChanged.multiple", { dates1: changedDates[0], date2: changedDates[1] });
                        } else {
                            displayMessage = t("notifications.shiftChanged.three", { date1: changedDates[0], date2: changedDates[1], date3: changedDates[2] });
                        }
                    } else {
                        displayMessage = t(translationKey, { startDate, endDate });
                    }

                    return (
                        <Box
                            key={notification.id}
                            sx={{ backgroundColor: "#f5f5f5", borderRadius: 2, p: 2, mb: 2 }}
                        >
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {displayMessage}
                            </Typography>

                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => {
                                    const id = String(notification.id);
                                    if (id) {
                                        void dispatch(confirmGenericNotificationThunk(id)).then(() => {
                                            dispatch(
                                                addAlert({
                                                    message: t("notifications.notificationConfirmed"),
                                                    severity: AlertSeverity.Success,
                                                    duration: 3000,
                                                })
                                            );
                                        });
                                    }
                                }}
                            >
                                {t("common.confirm")}
                            </Button>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderTransportOrderNotifications = () => {
        if (Object.keys(groupedNotifications).length === 0) return null;

        return Object.entries(groupedNotifications).map(([orderId, orderNotifications]) => (
            <Box key={orderId} sx={{ mb: 4 }}>
                <Grid2 container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                        {t("notifications.order")} {orderNotifications[0].transportOrderNumber}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<VisibilityIcon />}
                        onClick={() => {
                            navigate(`/transport/order/${orderId}`);
                        }}
                        sx={{
                            backgroundColor: "#1976d2",
                            textTransform: "none",
                        }}
                    >
                        {t("notifications.viewDetails")}
                    </Button>
                </Grid2>
                <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: 2, p: 2 }}>
                    {orderNotifications.map((notification: any) => {
                        const messageKey: string = String(notification.message);
                        const orderStateIndex: any = notification?.orderState;
                        const createdAt: string = String(notification.createdDateTime);
                        return (
                            <Box key={notification.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, borderBottom: "1px solid #e0e0e0", pb: 2, "&:last-child": { borderBottom: "none" } }}>
                                <Typography variant="body1" sx={{ wordBreak: "break-word", flex: 1 }}>
                                    {t(messageKey, {
                                        orderNro: notification.transportOrderNumber,
                                        vehicleName: notification.vehicleRegisterNumber,
                                        state: t(String(transportOrderStateNames[orderStateIndex])),
                                    })}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "nowrap", ml: 2 }}>
                                    {Base.dateToDayDateTimeStr(createdAt).replace(/^\w+\s/, "")}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        ));
    };

    const renderEmptyState = () => {
        if (workShiftNotifications.length > 0 || Object.keys(groupedNotifications).length > 0) {
            return null;
        }

        return (
            <Box>
                <Grid2 container gap={2} display="flex" justifyContent="center">
                    <Typography variant="body2" mt={2}>
                        {t("notifications.noNotifications")}
                    </Typography>
                </Grid2>
            </Box>
        );
    };

    const renderContent = () => {
        const workShiftContent = renderWorkShiftNotifications();
        const transportContent = renderTransportOrderNotifications();
        const emptyContent = renderEmptyState();

        const content: JSX.Element[] = [];
        if (workShiftContent) content.push(workShiftContent);
        if (transportContent) {
            if (Array.isArray(transportContent)) {
                content.push(...transportContent);
            } else {
                content.push(transportContent);
            }
        }
        if (emptyContent) content.push(emptyContent);

        return content.length > 0 ? content : <></>;
    };

    return (
        <MainLayout
            topComponent={
                    notifications.length > 0 ? (
                        <NotificationsPageTitle header={t("notifications.greeting")} text={user} />
                    ) : (
                        <PageTitle header={t("notifications.notifications")} />
                    )
                }
            hasPx
        >
            {renderContent()}
        </MainLayout>
    );
};
